import type { Contact, DomainSearch, MailboxOrder, OrderDraft, OrderState, Vendor } from "../../shared/mailboxOrders";
import { post, request } from "./api";

/** Buying sending domains and mailboxes through Smartlead's Smart Senders, for a product's cold email connection. */

const base = (productId: string) => `/products/${encodeURIComponent(productId)}`;
const order = (productId: string, orderId: string) => `${base(productId)}/mailbox-orders/${encodeURIComponent(orderId)}`;

export const mailboxOrdersApi = {
  vendors: (productId: string) => request<{ vendors: Vendor[] }>(`${base(productId)}/smart-senders/vendors`),
  /** Whether a name is free with this vendor, and close names that are. */
  domains: (productId: string, vendorId: number, name: string) =>
    request<DomainSearch>(`${base(productId)}/smart-senders/domains?vendorId=${vendorId}&name=${encodeURIComponent(name)}`),
  /** The product's orders, and the registration contact used last, to prefill the next one. */
  list: (productId: string) => request<{ orders: MailboxOrder[]; contact: Contact | null }>(`${base(productId)}/mailbox-orders`),
  /** A draft the server checked, with its quote. Nothing is bought yet. */
  draft: (productId: string, body: OrderDraft) => post<{ order: MailboxOrder }>(`${base(productId)}/mailbox-orders`, body),
  /** The draft changed in place, checked again. */
  change: (productId: string, orderId: string, body: OrderDraft) =>
    request<{ order: MailboxOrder }>(order(productId, orderId), { method: "PATCH", body: JSON.stringify(body) }),
  /** The owner's Buy: Smartlead charges the card on the Smartlead account. */
  place: (productId: string, orderId: string) => post<{ order: MailboxOrder }>(`${order(productId, orderId)}/place`),
  cancel: (productId: string, orderId: string) => post<{ order: MailboxOrder }>(`${order(productId, orderId)}/cancel`),
};

/** An order's state as the owner reads it, and the pill it wears. */
export const STATE_WORDS: Record<OrderState, { words: string; pill: string }> = {
  draft: { words: "Draft", pill: "plain" },
  placing: { words: "Buying", pill: "live" },
  unsure: { words: "Checking with Smartlead", pill: "tight" },
  placed: { words: "Bought: addresses on the way", pill: "live" },
  ready: { words: "Ready", pill: "good" },
  failed: { words: "Refused", pill: "bad" },
  cancelled: { words: "Cancelled", pill: "plain" },
};

/** States that change without the owner: the page reads the orders again while one is in them. */
export const MOVING: readonly OrderState[] = ["placing", "unsure", "placed"];

/** "$13", "$4.50": whole amounts without cents. */
export function price(cents: number, currency = "USD"): string {
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(whole ? 0 : 2)} ${currency}`;
  }
}

/** How long a vendor usually takes, in words: "about 8 hours". */
export function deliveryWords(hours: number | null): string | null {
  if (hours === null || !Number.isFinite(hours) || hours <= 0) return null;
  if (hours < 1.5) return "about an hour";
  if (hours < 36) return `about ${Math.round(hours)} hours`;
  return `about ${Math.round(hours / 24)} days`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const andList = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

export const mailboxCount = (o: Pick<MailboxOrder, "domains">) => o.domains.reduce((n, d) => n + d.mailboxes.length, 0);

/** The whole price in one sentence, as the owner sees it before Buy. */
export function priceSentence(o: MailboxOrder): string {
  const { currency, domainsCentsPerYear, mailboxesCentsPerMonth } = o.quote;
  return `${price(domainsCentsPerYear, currency)} a year for ${andList(o.domains.map((d) => d.domain))}, then ${price(mailboxesCentsPerMonth, currency)} a month for ${plural(mailboxCount(o), "sending address", "sending addresses")}, charged by Smartlead to the card on your Smartlead account.`;
}

/** The price in a short line for the orders list. */
export const priceShort = (o: MailboxOrder) => `${price(o.quote.domainsCentsPerYear, o.quote.currency)} a year, then ${price(o.quote.mailboxesCentsPerMonth, o.quote.currency)} a month`;

/** What a vendor's mailboxes are called. */
export function vendorTitle(v: Pick<Vendor, "provider" | "name">): string {
  if (v.provider === "Google") return "Google addresses";
  if (v.provider === "Outlook") return "Outlook addresses";
  if (v.provider === "SMTP") return "Private mail server";
  return v.name;
}

const KIND_ORDER: Record<Vendor["provider"], number> = { Google: 0, Outlook: 1, SMTP: 2, Other: 3 };

/** Google first, then Outlook, then the rest; within a kind, the vendor with the most orders first. */
export const sortVendors = (vs: Vendor[]) => vs.toSorted((a, b) => KIND_ORDER[a.provider] - KIND_ORDER[b.provider] || b.orders - a.orders);
